import React from "react";
import { Icon, Switch } from "../";
import { Pagination } from "../Data";
import { useGlobalUtils } from "@finitless/shared/lib";

const ProductsList = ({
  products,
  productsPagination,
  isLoadingProducts,
  productPage,
  onPageChange,
  selectedProduct,
  onSelectProduct,
  productSearchInput,
  onSearchChange,
  onClearSearch,
  hasPosIntegration,
  selectedCategoryId,
  onCreateProduct,
  onToggleProduct,
  isLoadingToggle,
  showCategoryContext = false,
  searchPlaceholder = "Search products...",
  categoryLabelPrefix = "",
  uncategorizedLabel = "",
}) => {
  const { parsePrice } = useGlobalUtils();

  const handleProductClick = (product) => {
    // Siempre abrir modal de extras (que tiene tabs de Extras y Edit Product)
    onSelectProduct(product);
  };

  const getCategoryName = (product) => {
    const categoryName =
      product?.category_name ??
      product?.categoryName ??
      product?.category?.name ??
      product?.category?.title;

    if (categoryName) {
      return categoryName;
    }

    return uncategorizedLabel;
  };

  return (
    <div className="flex h-full flex-col rounded-lg border border-gray-800 bg-gray-900">
      {/* Header */}
      <div className="border-b border-gray-800 p-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center space-x-2">
            <Icon name="Package" className="h-5 w-5 text-gray-400" />
            <h3 className="text-lg font-semibold text-white">Products</h3>
          </div>

          <div className="flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:items-center sm:justify-end">
            {/* Product Search */}
            <div className="relative w-full sm:w-80">
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                <Icon name="Search" className="h-4 w-4 text-gray-400" />
              </div>
              <input
                type="text"
                placeholder={searchPlaceholder}
                value={productSearchInput}
                onChange={(e) => onSearchChange(e.target.value)}
                className="w-full rounded-lg border border-gray-700 bg-gray-800 py-2 pr-10 pl-10 text-sm text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:outline-none"
              />
              {productSearchInput && (
                <button
                  onClick={onClearSearch}
                  className="absolute inset-y-0 right-0 flex items-center pr-3"
                >
                  <Icon
                    name="X"
                    className="h-4 w-4 text-gray-400 hover:text-white"
                  />
                </button>
              )}
            </div>

            {!hasPosIntegration && selectedCategoryId && (
              <div className="sm:ml-auto">
                <button
                  className="flex items-center gap-1 rounded bg-blue-600 px-3 py-2 text-sm text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
                  onClick={onCreateProduct}
                  disabled={!selectedCategoryId}
                >
                  <Icon name="Plus" className="h-4 w-4" /> Add Product
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Products List */}
      <div className="flex-1 overflow-y-auto p-4">
        {isLoadingProducts ? (
          <div className="flex h-full items-center justify-center">
            <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-blue-500"></div>
          </div>
        ) : products.length > 0 ? (
          <>
            <div className="space-y-2">
              {products.map((product) => (
                <div
                  key={product.id}
                  className={`flex cursor-pointer items-center justify-between rounded-lg border p-3 transition-colors ${
                    selectedProduct?.id === product.id
                      ? "border-blue-500 bg-blue-500/10"
                      : "border-gray-700 bg-gray-800 hover:border-gray-600"
                  }`}
                  onClick={() => handleProductClick(product)}
                >
                  <div className="min-w-0 flex-1">
                    <h4 className="truncate text-sm font-medium text-white">
                      {product.name}
                    </h4>
                    {product.description && (
                      <p className="mt-1 line-clamp-1 truncate text-xs text-gray-400">
                        {product.description}
                      </p>
                    )}
                    {showCategoryContext && (
                      <p className="mt-1 text-xs text-blue-300">
                        {categoryLabelPrefix}: {getCategoryName(product)}
                      </p>
                    )}
                  </div>
                  <div className="ml-4 flex items-center gap-3">
                    <span className="text-sm font-semibold text-green-400">
                      {parsePrice(product.price || 0)}
                    </span>
                    <Switch
                      checked={!!product.enabled}
                      onCheckedChange={(value) => {
                        if (onToggleProduct) {
                          onToggleProduct(product.id, value);
                        }
                      }}
                      disabled={isLoadingToggle}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                </div>
              ))}
            </div>

            {/* Product Pagination */}
            {productsPagination.total_pages > 1 && (
              <div className="mt-4 border-t border-gray-800 pt-4">
                <Pagination
                  currentPage={productPage}
                  totalPages={productsPagination.total_pages}
                  onPageChange={onPageChange}
                  maxVisiblePages={5}
                />
              </div>
            )}
          </>
        ) : (
          <div className="flex h-full items-center justify-center">
            <div className="text-center">
              <Icon
                name="Package"
                className="mx-auto mb-2 h-12 w-12 text-gray-400"
              />
              <p className="text-sm text-gray-400">No products found</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductsList;
